import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useScrollToTop } from '../hooks/useScrollToTop';

const UnsubscribePage: React.FC = () => {
  // Scroll to top when page loads
  useScrollToTop();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid unsubscribe link. Please contact us if you need help.');
      return;
    }

    const unsubscribe = async () => {
      try {
        const apiUrl = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:5001/api/newsletter/unsubscribe'
          : '/api/newsletter/unsubscribe';

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const result = await response.json();

        if (result.success) {
          setStatus('success');
          setMessage(result.message);
        } else {
          setStatus('error');
          setMessage(result.message);
        }
      } catch (error) {
        setStatus('error');
        setMessage('Failed to unsubscribe. Please try again later.');
      }
    };

    unsubscribe();
  }, [searchParams]);

  return (
    <>
      <Helmet>
        <title>Unsubscribe - Destress Daily</title>
        <meta name="description" content="Unsubscribe from Destress Daily newsletter" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-gradient-to-r from-health-500 to-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">DH</span>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Newsletter Unsubscribe
            </h2>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {status === 'loading' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Processing your request...</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Successfully Unsubscribed
                </h3>
                <p className="mt-2 text-gray-600">
                  {message}
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  You will no longer receive emails from Destress Daily.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Unsubscribe Failed
                </h3>
                <p className="mt-2 text-gray-600">
                  {message}
                </p>
                <p className="mt-4 text-sm text-gray-500">
                  If you continue to receive emails, please contact us directly.
                </p>
              </div>
            )}

            <div className="mt-8 text-center">
              <Link
                to="/"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                ← Back to Home
              </Link>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500">
              Changed your mind?{' '}
              <Link to="/" className="text-primary-600 hover:text-primary-700">
                Subscribe again
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnsubscribePage; 